/**
 * useDemoClock.ts
 *
 * The demo clock, as the app sees it (card D1, plan §3.5; the player's own
 * REST half added by card D2, plan §4.7/§4.8). Three parts in one module,
 * because they are one contract:
 *
 *  - `attachDemoClockListeners(socket)` — the ONE listener set for the whole
 *    app. `stores/socket.ts` calls it when it opens or replaces a hub socket,
 *    next to the socket's own `connect`/`disconnect` handlers. `demo:clock` is
 *    the 1 Hz frame while the demo plays; `demo:state` is every mode, script or
 *    playback change, and a rate change or a seek is exactly when a frame that
 *    was only extrapolated has to be re-anchored (a widget would otherwise lag
 *    or snap). Both feed `services/clockSync`, which every clock surface already
 *    reads through `serverNow()` — so nothing else in the app listens.
 *  - `useDemoClock()` — the reactive view D2's player and FAB read: `enabled`,
 *    `iso`, `rate`, `playing`, `dayIndex`, `dayLabel`, plus the raw `frame` and
 *    the ticking `nowMs`. It adds no listener: it re-derives from `clockSync`
 *    once a second, and again on every frame change (so a seek shows at once).
 *    Since D2 it also carries the **mode** block and the **playback** verbs, so
 *    a surface that shows the clock can also move it.
 *  - the REST mirror: `GET /api/demo` (`status`), `playback(action, params)` and
 *    `setMode(enabled)`. One copy for the whole app, one request in flight at a
 *    time, and — for a surface that opts in (`useDemoClock({ poll: true })`) —
 *    one 1 Hz poll while a player is open, silent while the document is hidden.
 *
 * `serverNow()` is the authority for the INSTANT: this composable never computes
 * sim time itself, it only ticks it for Vue. Anything that needs the instant
 * outside a template should keep calling `serverNow()` (`useServerClock`,
 * `syncSunClock`, zone audio all do). The REST mirror is not a second clock — it
 * is the hub's word on MODE (is this hub a demo, does it want a FAB) and on the
 * verbs, which no socket event answers for a client that has just loaded.
 *
 * The transport is imported lazily (`services/demoPlayback`), on purpose:
 * `stores/socket.ts` imports THIS module on every boot, while `services/api.ts`
 * imports `stores/auth.ts` — a static edge here would close the loop
 * socket → useDemoClock → api → auth → socket. The fetch is only ever reached
 * from a demo surface, so the late import costs one resolved promise and keeps
 * the boot graph exactly as D1 left it.
 */

import { computed, getCurrentScope, onScopeDispose, ref, shallowRef, type ComputedRef, type Ref } from 'vue';
import {
  applyDemoClockEvent,
  applyDemoStatusEvent,
  onDemoFrameChange,
  serverDemoDay,
  serverFrame,
  serverNow,
  serverRate,
  type DemoClockDay,
  type DemoClockFrame,
} from '../services/clockSync';
import { applyDemoPlayback, demoErrorText, readDemoStatus, type DemoStatus } from '../utils/demoPlayerModel';
import type { HubSocket } from '../stores/socket';
import { tt } from '../utils/consoleText';
import { pushDemoRunLog, pushDemoRunLogWarning } from './useDemoRunLog';

/** One second is the player's own unit; nothing here needs sub-second copy. */
const TICK_MS = 1_000;

/** How often an open player surface re-reads the hub's status (card D2). */
const STATUS_POLL_MS = 1_000;

/**
 * How long an answer is reused for a surface that mounts (not for a poll or a
 * Refresh press): the layout, the FAB and a deep-linked page mount in the same
 * tick, and three GETs for one answer would be three times the traffic for the
 * same second of truth.
 */
const STATUS_REUSE_MS = 2_000;

/** The verbs `POST /api/demo/playback` answers (D0, plus D8a's seek/day/cut/replayCut). */
export type DemoPlaybackAction = 'play' | 'pause' | 'speed' | 'seek' | 'day' | 'cut' | 'replayCut';

/** What a playback press may carry (mirrors `services/demoPlayback.ts`, kept local so the boot graph stays lazy). */
export interface DemoPlaybackParams {
  /** `action: 'speed'` — one of `DEMO_SPEED_PRESETS`. */
  rate?: number;
  /** `action: 'seek'` — `HH:MM` on the current day, an ISO instant, or epoch ms. */
  at?: string;
  /** `action: 'seek'` — the day to seek inside. */
  dayId?: string;
  /** `action: 'day'` — the 0-based day index. */
  index?: number;
  /** `action: 'cut' | 'replayCut'` — the cut's own id. */
  cutId?: string;
  /** `action: 'replayCut'` — sim minutes of lead-in (the hub's default is 2). */
  leadInMin?: number;
}

/**
 * Subscribe a hub socket to the demo clock.
 *
 * Returns the detach — `stores/socket.ts` keeps it so a deliberate disconnect
 * (logout, hub switch) removes these two listeners with the socket they belong
 * to rather than leaving them on a dead handle.
 */
export function attachDemoClockListeners(socket: HubSocket): () => void {
  const onClock = (payload: unknown): void => {
    applyDemoClockEvent(payload);
  };
  const onState = (payload: unknown): void => {
    applyDemoStatusEvent(payload);
    // `demo:state` IS the whole status (mode + clock + frame, D0), so it keeps
    // the REST mirror honest without waiting for a poll: an operator who flips
    // demo mode in another window sees the FAB appear here at once.
    adoptStatus(payload);
  };

  socket.on('demo:clock', onClock);
  socket.on('demo:state', onState);

  return () => {
    socket.off('demo:clock', onClock);
    socket.off('demo:state', onState);
  };
}

/**
 * App-wide mirror of the frame, so any number of consumers cost one timer: the
 * first `useDemoClock()` starts it, the last one to unmount stops it.
 */
const nowMs: Ref<number> = ref(serverNow());
const frame: Ref<DemoClockFrame | null> = shallowRef(serverFrame());
const day: Ref<DemoClockDay | null> = shallowRef(serverDemoDay());

let ticker: ReturnType<typeof setInterval> | null = null;
let stopFrameWatch: (() => void) | null = null;
let consumers = 0;

/** The hub's last word on the demo mode and its clock; `null` until it answers. */
const status: Ref<DemoStatus | null> = shallowRef(null);
const statusLoading = ref(false);
const playbackPending = ref(false);
const statusError = ref<string | null>(null);

/** One request at a time for the whole app — a poll and a press never race. */
let statusInFlight: Promise<DemoStatus | null> | null = null;

/** When the status was last read from the hub (mount-time reuse, see below). */
let statusLoadedAt = 0;

/**
 * Surfaces that asked for the 1 Hz poll, counted while their scope lives (card
 * D2: "one poll at 1 Hz while a player surface is open, stop when hidden").
 */
let pollConsumers = 0;
let statusPoll: ReturnType<typeof setInterval> | null = null;
let stopVisibilityWatch: (() => void) | null = null;

function refresh(): void {
  frame.value = serverFrame();
  day.value = serverDemoDay();
  nowMs.value = serverNow();
}

function retain(): void {
  consumers += 1;
  if (consumers > 1) return;
  refresh();
  stopFrameWatch = onDemoFrameChange(refresh);
  ticker = setInterval(refresh, TICK_MS);
}

function release(): void {
  consumers = Math.max(0, consumers - 1);
  if (consumers > 0) return;
  if (ticker !== null) {
    clearInterval(ticker);
    ticker = null;
  }
  stopFrameWatch?.();
  stopFrameWatch = null;
}

/** A surface's own line for a request nothing readable came back from. */
function refusalText(error: unknown): string {
  return demoErrorText(error) ?? tt('The demo clock did not answer', 'Demo ura se ni odzvala');
}

/** Adopt a payload that carries the whole status (`GET /api/demo`, `demo:state`). */
function adoptStatus(payload: unknown): void {
  const next = readDemoStatus(payload);
  if (!next) return;
  status.value = next;
  statusError.value = null;
}

/**
 * Re-read `GET /api/demo`. Single-flight: a poll tick, a button press and a
 * surface mounting in the same second share one request. A failure lands in
 * `error` and never clears a status the hub already gave — a flaky poll must
 * not blank a running player.
 *
 * `force` is for the poll and for a Refresh press; a surface that is merely
 * mounting reuses an answer younger than `STATUS_REUSE_MS`.
 */
async function refreshStatus(force = false): Promise<DemoStatus | null> {
  if (statusInFlight) return statusInFlight;
  if (!force && status.value !== null && Date.now() - statusLoadedAt < STATUS_REUSE_MS) return status.value;
  statusInFlight = (async () => {
    statusLoading.value = true;
    try {
      const { fetchDemoStatus } = await import('../services/demoPlayback');
      const next = await fetchDemoStatus();
      if (next) {
        status.value = next;
        statusLoadedAt = Date.now();
        statusError.value = null;
      } else {
        statusError.value = refusalText(null);
      }
      return next;
    } catch (error) {
      statusError.value = refusalText(error);
      return null;
    } finally {
      statusLoading.value = false;
      statusInFlight = null;
    }
  })();
  return statusInFlight;
}

/**
 * One run-log line per press (card D8b): the verb, what it named, and the sim
 * instant the hub answered with. Written HERE rather than at each button so a
 * surface can never move the clock without the log knowing — that is what makes
 * the log an account of the run rather than of one tab's buttons.
 */
function playbackLogLabel(action: DemoPlaybackAction, params: DemoPlaybackParams): string {
  switch (action) {
    case 'speed':
      return params.rate === undefined ? '' : `${Math.round(params.rate)}×`;
    case 'seek':
      return params.at ?? '';
    case 'day':
      return String((params.index ?? 0) + 1);
    case 'cut':
    case 'replayCut':
      return params.cutId ?? '';
    default:
      return '';
  }
}

/**
 * `POST /api/demo/playback` with an optimistic paint: play/pause/speed show at
 * once (the `demo:state` the hub broadcasts re-anchors the clock a moment later),
 * and a refusal **rolls the paint back and re-reads the hub** rather than leaving
 * a button claiming a state the hub never took. Resolves the hub's status, or
 * `null` when the press was refused (the reason is in `error`).
 */
async function runPlayback(action: DemoPlaybackAction, params: DemoPlaybackParams = {}): Promise<DemoStatus | null> {
  if (playbackPending.value) return null;
  playbackPending.value = true;
  statusError.value = null;
  const before = status.value;
  if (before && (action === 'play' || action === 'pause' || action === 'speed')) {
    status.value = applyDemoPlayback(before, action, params.rate);
  }
  try {
    const { sendDemoPlayback } = await import('../services/demoPlayback');
    const next = await sendDemoPlayback(action, params);
    if (next) status.value = next;
    else statusError.value = refusalText(null);
    // The log takes the press whether or not the hub took it: a refused seek is
    // exactly the line an operator needs when a demo "did not move".
    if (next) pushDemoRunLog({ kind: action, label: playbackLogLabel(action, params), simIso: next.clock.iso });
    return next;
  } catch (error) {
    statusError.value = refusalText(error);
    pushDemoRunLogWarning(statusError.value);
    if (before) status.value = before;
    // The hub's own word wins over the rollback: a 400 still tells us where the
    // clock actually is.
    void refreshStatus(true);
    return null;
  } finally {
    playbackPending.value = false;
  }
}

/**
 * `POST /api/demo/mode` — enrol in / leave demo mode. Same optimistic rule as
 * `runPlayback`: the FAB appears the moment the press lands, and a refusal puts
 * it back. Resolves the hub's status, or `null` on a refusal.
 */
async function runMode(enabled: boolean): Promise<DemoStatus | null> {
  if (playbackPending.value) return null;
  playbackPending.value = true;
  statusError.value = null;
  try {
    const { sendDemoMode } = await import('../services/demoPlayback');
    const next = await sendDemoMode(enabled);
    if (next) status.value = next;
    else statusError.value = refusalText(null);
    if (next) {
      pushDemoRunLog({
        kind: 'mode',
        label: enabled ? tt('demo mode on', 'demo način vklopljen') : tt('demo mode off', 'demo način izklopljen'),
        simIso: next.clock.iso,
      });
    }
    return next;
  } catch (error) {
    statusError.value = refusalText(error);
    pushDemoRunLogWarning(statusError.value);
    void refreshStatus(true);
    return null;
  } finally {
    playbackPending.value = false;
  }
}

function retainPoll(): void {
  pollConsumers += 1;
  if (pollConsumers > 1) return;
  void refreshStatus(true);
  statusPoll = setInterval(() => {
    // A hidden document asks for nothing; the next visible tick picks the player
    // straight back up, so a demo left in a background tab costs no traffic.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void refreshStatus(true);
  }, STATUS_POLL_MS);
  if (typeof document !== 'undefined') {
    const onVisibility = (): void => {
      if (document.visibilityState !== 'hidden') void refreshStatus(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    stopVisibilityWatch = () => document.removeEventListener('visibilitychange', onVisibility);
  }
}

function releasePoll(): void {
  pollConsumers = Math.max(0, pollConsumers - 1);
  if (pollConsumers > 0) return;
  if (statusPoll !== null) {
    clearInterval(statusPoll);
    statusPoll = null;
  }
  stopVisibilityWatch?.();
  stopVisibilityWatch = null;
}

/** What a player surface may ask for when it mounts. */
export interface DemoClockOptions {
  /**
   * Re-read the hub's status once a second while this surface lives (the mini
   * player, the Settings → Demo → Playback tab). The collapsed FAB does not: its
   * clock comes from the frame, which already ticks once a second.
   */
  poll?: boolean;
}

export interface DemoClockView {
  /** Demo mode is on and a frame is live — every clock in the app is sim time. */
  enabled: ComputedRef<boolean>;
  /** The sim instant as the hub spells it (UTC ISO; format it locally). */
  iso: ComputedRef<string>;
  /** Sim-ms per real-ms: 1 outside a demo, 288× by default in one. */
  rate: ComputedRef<number>;
  /** Whether sim time is advancing; a paused demo freezes every clock. */
  playing: ComputedRef<boolean>;
  /** The script day, 0-based (the player words it 1-based). */
  dayIndex: ComputedRef<number>;
  /** “Day 1”, or the script's own label for the day once D3 sends one. */
  dayLabel: ComputedRef<string>;
  /** The sim instant, re-read every second and on every frame change. */
  nowMs: Ref<number>;
  /** The frame this client follows (null outside demo mode). */
  frame: Ref<DemoClockFrame | null>;
  /** The demo day as the last broadcast named it. */
  day: Ref<DemoClockDay | null>;
  /** The hub's whole status (`GET /api/demo`), `null` until it answers. */
  status: Ref<DemoStatus | null>;
  /** Is this hub a demo? A live frame counts, even before the status lands. */
  modeEnabled: ComputedRef<boolean>;
  /** Does the hub want the demo FAB shown? (strict: no status, no FAB) */
  showFab: ComputedRef<boolean>;
  /** Does the demo FAB stand in for the agent's? */
  replaceAgentFab: ComputedRef<boolean>;
  /** A status request is in flight (a surface can dim its controls). */
  statusLoading: ComputedRef<boolean>;
  /** A playback or mode press is in flight. */
  playbackPending: ComputedRef<boolean>;
  /** Why the last request was refused, ready to paint. */
  error: Ref<string | null>;
  /** Read the status now, unmistakably (a Refresh button, a retry). */
  refresh: () => Promise<DemoStatus | null>;
  /**
   * Read the status when a surface mounts — an answer younger than
   * `STATUS_REUSE_MS` is reused, so three surfaces mounting together cost one
   * request. This is what a player surface calls from its own watcher, never the
   * module's `retain()` (a clock consumer must not spend a request by existing).
   */
  ensureLoaded: () => Promise<DemoStatus | null>;
  /** Play / pause / speed / seek — admin work, refused by the hub otherwise. */
  playback: (action: DemoPlaybackAction, params?: DemoPlaybackParams) => Promise<DemoStatus | null>;
  /** Enrol in / leave demo mode. */
  setMode: (enabled: boolean) => Promise<DemoStatus | null>;
}

export function useDemoClock(options: DemoClockOptions = {}): DemoClockView {
  retain();
  if (getCurrentScope()) onScopeDispose(release);

  if (options.poll) {
    retainPoll();
    if (getCurrentScope()) onScopeDispose(releasePoll);
  }

  const enabled = computed(() => frame.value !== null);
  const iso = computed(() => new Date(nowMs.value).toISOString());
  const rate = computed(() => frame.value?.rate ?? serverRate());
  const playing = computed(() => frame.value?.playing ?? false);
  const dayIndex = computed(() => day.value?.dayIndex ?? 0);
  const dayLabel = computed(() => {
    const label = day.value?.label;
    if (label) return label;
    const number = dayIndex.value + 1;
    return tt(`Day ${number}`, `Dan ${number}`);
  });

  // The mode block. `modeEnabled` trusts a live frame as well as the status: a
  // hub that answers `clock:ping` with a demo frame IS a demo, whatever a failed
  // GET said. The two FAB switches stay strict — a hub that never says `showFab`
  // gets no FAB rather than an invention of ours.
  const modeEnabled = computed(() => status.value?.mode.enabled ?? frame.value !== null);
  const showFab = computed(() => modeEnabled.value && (status.value?.mode.showFab ?? false));
  const replaceAgentFab = computed(() => modeEnabled.value && (status.value?.mode.replaceAgentFab ?? false));

  return {
    enabled,
    iso,
    rate,
    playing,
    dayIndex,
    dayLabel,
    nowMs,
    frame,
    day,
    status,
    modeEnabled,
    showFab,
    replaceAgentFab,
    statusLoading: computed(() => statusLoading.value),
    playbackPending: computed(() => playbackPending.value),
    error: statusError,
    refresh: () => refreshStatus(true),
    ensureLoaded: () => refreshStatus(false),
    playback: runPlayback,
    setMode: runMode,
  };
}
